import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Upload,
  FileText,
  Image as ImageIcon,
  Trash2,
  Download,
  Loader2,
} from 'lucide-react';
import {
  appointmentAttachmentUrl,
  useAppointmentAttachments,
  useDeleteAppointmentAttachment,
  useUploadAppointmentAttachment,
  type AppointmentAttachmentRow,
} from '@/hooks/data/useAppointmentAttachments';
import { useAuth } from '@/hooks/useAuth';
import { formatDateTime } from '@/lib/formatLocale';

// Batch AH faza 3d — operator-uploaded attachments shown below the
// system-generated documents on the appointment detail surface.
// Accepts PDF + JPG/PNG/WEBP/HEIC, 20 MB cap. Uploader sees a delete
// button on their own files; admins see it on every file.

const ACCEPT =
  'application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif';
const MAX_BYTES = 20 * 1024 * 1024;

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function AttachmentRow({
  appointmentId,
  row,
  canDelete,
  onDelete,
}: {
  appointmentId: string;
  row: AppointmentAttachmentRow;
  canDelete: boolean;
  onDelete: (id: string) => void;
}) {
  const { t } = useTranslation();
  const isImage = row.mime_type.startsWith('image/');
  const url = appointmentAttachmentUrl(appointmentId, row.id);
  const created = formatDateTime(new Date(row.created_at), {
    dateStyle: 'short',
    timeStyle: 'short',
  });

  return (
    <li className="flex items-center gap-3 rounded-lg border border-line bg-paper-2 p-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-paper text-ink-3">
        {isImage ? (
          <ImageIcon className="h-5 w-5" />
        ) : (
          <FileText className="h-5 w-5" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-ink">{row.file_name}</p>
        <p className="text-xs text-ink-3">
          {fmtSize(row.size_bytes)} · {created}
        </p>
        {row.description && (
          <p className="mt-0.5 line-clamp-2 text-xs text-ink-2">
            {row.description}
          </p>
        )}
      </div>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        title={t('common.download', 'Herunterladen')}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-3 transition hover:bg-paper-3 hover:text-ink"
      >
        <Download className="h-4 w-4" />
      </a>
      {canDelete && (
        <button
          type="button"
          onClick={() => onDelete(row.id)}
          title={t('common.delete', 'Löschen')}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-3 transition hover:bg-red-50 hover:text-red-600"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </li>
  );
}

export function AppointmentAttachments({
  appointmentId,
}: {
  appointmentId: string;
}) {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const { data: rows = [], isLoading } = useAppointmentAttachments(appointmentId);
  const upload = useUploadAppointmentAttachment(appointmentId);
  const del = useDeleteAppointmentAttachment(appointmentId);
  const fileRef = useRef<HTMLInputElement>(null);
  const [description, setDescription] = useState('');

  const onPick = () => fileRef.current?.click();

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error(
        t(
          'attachments.error.too_large',
          `Datei zu groß (max ${fmtSize(MAX_BYTES)})`,
        ),
      );
      e.target.value = '';
      return;
    }
    try {
      await upload.mutateAsync({
        file,
        description: description.trim() || undefined,
      });
      setDescription('');
      toast.success(t('attachments.uploaded', 'Datei hochgeladen.'));
    } catch (cause) {
      const msg = (cause as Error).message;
      if (msg.startsWith('infected:')) {
        toast.error(
          t('attachments.error.infected', 'Virus erkannt — Upload abgelehnt.'),
        );
      } else if (msg === 'unsupported_media_type') {
        toast.error(
          t('attachments.error.unsupported', 'Dateityp nicht unterstützt.'),
        );
      } else {
        toast.error(t('attachments.error.generic', 'Upload fehlgeschlagen.'));
      }
    } finally {
      e.target.value = '';
    }
  };

  const onDelete = async (id: string) => {
    if (!window.confirm(t('attachments.confirm_delete', 'Datei wirklich löschen?'))) {
      return;
    }
    try {
      await del.mutateAsync(id);
      toast.success(t('attachments.deleted', 'Datei gelöscht.'));
    } catch {
      toast.error(t('attachments.error.delete', 'Löschen fehlgeschlagen.'));
    }
  };

  return (
    <div className="mt-4 rounded-xl border border-line bg-card p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h4 className="font-display text-base text-ink">
          {t('attachments.title', 'Manuelle Anhänge')}
        </h4>
        <span className="text-xs text-ink-3">
          {rows.length}{' '}
          {rows.length === 1
            ? t('attachments.file', 'Datei')
            : t('attachments.files', 'Dateien')}
        </span>
      </div>

      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t(
            'attachments.description_placeholder',
            'Kurze Beschreibung (optional)',
          )}
          className="flex-1 rounded-md border border-line bg-paper-2 px-3 py-2 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
          maxLength={500}
        />
        <button
          type="button"
          onClick={onPick}
          disabled={upload.isPending}
          className="inline-flex h-10 items-center gap-2 rounded-md bg-ink px-4 text-sm font-semibold text-paper transition hover:bg-ink/90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {upload.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          {t('attachments.upload', 'Hochladen')}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT}
          onChange={onFile}
          className="hidden"
        />
      </div>
      <p className="mb-3 text-[11px] text-ink-3">
        {t(
          'attachments.hint',
          'PDF, JPG, PNG, HEIC · max. 20 MB pro Datei',
        )}
      </p>

      {isLoading ? (
        <p className="py-4 text-center text-sm text-ink-3">
          {t('common.loading', 'Lädt …')}
        </p>
      ) : rows.length === 0 ? (
        <p className="py-4 text-center text-sm text-ink-3">
          {t('attachments.empty', 'Noch keine manuellen Anhänge.')}
        </p>
      ) : (
        <ul className="space-y-2">
          {rows.map((row) => (
            <AttachmentRow
              key={row.id}
              appointmentId={appointmentId}
              row={row}
              canDelete={
                profile?.role === 'admin' || row.uploaded_by === profile?.user_id
              }
              onDelete={onDelete}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
